<template>
  <!-- Font Awesome: font-class backend, rendered as an empty <i>. -->
  <i
    v-if="isFa"
    :class="[faClasses, { 'pointer-events-none opacity-40': disabled }]"
    :style="style"
    :title="title"
    :aria-hidden="clickable ? undefined : 'true'"
    :aria-label="clickable ? ariaLabel : undefined"
    :role="clickable ? 'button' : undefined"
    :tabindex="clickable ? 0 : undefined"
    @click="onClick"
    @keydown="onKeydown"
  />
  <span
    v-else
    :class="[lucideClasses, { 'pointer-events-none opacity-40': disabled }]"
    :style="style"
    :title="title"
    :aria-hidden="clickable ? undefined : 'true'"
    :aria-label="clickable ? ariaLabel : undefined"
    :role="clickable ? 'button' : undefined"
    :tabindex="clickable ? 0 : undefined"
    @click="onClick"
    @keydown="onKeydown"
    v-html="svg"
    v-bind="$attrs"
  />
</template>

<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue'

import { renderLucideSvg, resolveLucideIcon } from './lucideIcons'
import { resolveThemeColor } from './themeColor'

/**
 * UiIcon — framework-agnostic icon wrapper.
 *
 * Two backends, both dependency-light and Vue-version-neutral:
 * - Font Awesome (@fortawesome/fontawesome-free): class-based font icons.
 *   Detected by the `fa-` / `fa ` / `fa-solid ` prefix; the CSS is imported
 *   once in main.ts. Used by FontAwesomeIconPicker and any `fa-*` call site.
 * - Lucide (vanilla `lucide` package): inline SVG rendered from icon data
 *   (`IconNode = [tag, attrs][]`), no Vue-specific dependency. Call sites pass
 *   a Lucide kebab name: `<UiIcon name="plus" />`, `<UiIcon name="trash-2" />`.
 *
 * Legacy Material Design Icons ligature names (snake_case, e.g. `arrow_back`,
 * `cloud_upload`) are still accepted as a render-time alias for icon values
 * sourced from store/config data that predate the migration; they resolve
 * through `mdiToLucide` in `lucideIcons.ts`. New call sites should use the
 * Lucide kebab name directly.
 *
 * The backend is isolated behind this component. At the Vue 3 flip the Lucide
 * internals can optionally move to `lucide-vue-next` (Vue 3 components) without
 * touching call sites — the `name` prop contract is unchanged.
 */
const props = withDefaults(
  defineProps<{
    name?: string
    size?: 'x-small' | 'small' | 'default' | 'large' | 'x-large' | number
    color?: string
    title?: string
    left?: boolean
    right?: boolean
    dark?: boolean
    disabled?: boolean
    /** Accessible name of a clickable icon, when no visible `title` is wanted
     * (e.g. the icon already sits in a UiTooltip). */
    ariaLabel?: string
    /** Explicitly mark the icon as an interactive control. When true (or
     * when a click listener is attached), the icon gets role="button",
     * tabindex="0", and Enter/Space activation. */
    clickable?: boolean
  }>(),
  {
    left: false,
    right: false,
    dark: false,
    disabled: false,
    clickable: false,
  },
)

const emit = defineEmits<(e: 'click', event: MouseEvent) => void>()

const instance = getCurrentInstance()
// An icon is "clickable" if the `clickable` prop is set OR a parent
// attached a `@click` listener (which Vue 3 stores as `onClick` in vnode
// props, not in $attrs when defineEmits is used).
const clickable = computed(() => {
  if (props.clickable) return true
  const vnode = instance?.vnode
  return !!(vnode && 'onClick' in (vnode.props ?? {}))
})

function onClick(event: MouseEvent) {
  if (props.disabled) return
  emit('click', event)
}

function onKeydown(event: KeyboardEvent) {
  if (!clickable.value || props.disabled) return
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    emit('click', event as unknown as MouseEvent)
  }
}
const isFa = computed(() => {
  const n = (props.name ?? '').trim()
  return n.startsWith('fa-') || n.startsWith('fa ')
})

const sizePx = computed(() => {
  if (typeof props.size === 'number') return props.size
  const map: Record<string, number> = {
    'x-small': 12,
    small: 16,
    default: 24,
    large: 36,
    'x-large': 48,
  }
  return map[props.size ?? 'default'] ?? 24
})

const resolvedColor = computed(() => resolveThemeColor(props.color))

const faClasses = computed(() => {
  // Font Awesome: name like "fa-edit" or "fa-solid fa-magnifying-glass"
  const parts = (props.name ?? '').trim().split(/\s+/)
  const cls: string[] = []
  if (parts.length === 1) {
    // bare "fa-edit" → assume solid
    cls.push('fa-solid', parts[0])
  } else {
    cls.push(...parts)
  }
  if (props.left) cls.push('mr-1')
  if (props.right) cls.push('ml-1')
  return cls.join(' ')
})

const lucideClasses = computed(() => {
  const cls: string[] = ['lucide-icon']
  if (props.left) cls.push('mr-1')
  if (props.right) cls.push('ml-1')
  return cls.join(' ')
})

const svg = computed(() => {
  const node = resolveLucideIcon(props.name ?? '')
  if (!node) return ''
  return renderLucideSvg(node, sizePx.value, resolvedColor.value)
})

const style = computed(() => ({
  fontSize: `${sizePx.value}px`,
  color: resolvedColor.value,
  verticalAlign: 'middle',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
}))
</script>

<style scoped>
/* Lucide icons are inline SVGs rendered via v-html; the span wrapper's
 * layout is set by the inline :style, but the inner svg needs display:block
 * to avoid inline-SVG baseline gaps. Not expressible as a Tailwind utility. */
.lucide-icon :deep(svg) {
  display: block;
}
</style>
